import React from "react";
import { useI18n } from "@/lib/i18n";

const CONTENT = {
  it: {
    overline: "Chi siamo",
    title: "Un progetto nato dall'incontro tra scienza, natura e cura.",
    lead:
      "BioNutrix nasce dall'incontro tra esperienza nel settore chimico-farmaceutico, passione per il benessere e desiderio di creare soluzioni affidabili per persone e animali.",
    paragraphs: [
      "Il nostro lavoro ricerca ogni giorno un equilibrio tra natura, conoscenza e innovazione, con particolare attenzione alla qualità delle formulazioni e alla trasparenza verso chi ci sceglie.",
      "Vogliamo dare al mercato pet e — in futuro — anche a quello dedicato alle persone, prodotti sviluppati con criteri chiari, senza claim eccessivi e con la stessa cura che riserveremmo a un animale o a una persona di famiglia.",
      "Il team BioNutrix nasce da percorsi differenti — chimica, farmaceutica, comunicazione — uniti dal desiderio di creare formulazioni pensate per accompagnare la quotidianità.",
    ],
    values_title: "Missione, visione, valori",
    mission_h: "Missione",
    mission_t: "Portare formulazioni chiare e prudenti nella routine quotidiana di persone e animali.",
    vision_h: "Visione",
    vision_t: "Un'azienda italiana riconosciuta per qualità, trasparenza e rispetto verso chi la sceglie.",
    values_h: "Valori",
    values_t: "Competenza scientifica, ingredienti selezionati, comunicazione responsabile, cura.",
    territory_over: "Sede",
    territory_h: "Un territorio, un'identità.",
    territory_t:
      "La nostra sede è a Porto Empedocle, in Sicilia — un territorio in cui natura e ricerca si incrociano da sempre. Da qui coordiniamo lo sviluppo delle formulazioni, la produzione italiana e la relazione con i partner commerciali su tutto il territorio nazionale.",
  },
  en: {
    overline: "About us",
    title: "A project born from the encounter of science, nature and care.",
    lead:
      "BioNutrix was born from the meeting of experience in the chemical-pharmaceutical sector, a passion for wellbeing, and a desire to create trustworthy solutions for people and animals.",
    paragraphs: [
      "Our work seeks a daily balance between nature, knowledge and innovation, with particular attention to the quality of formulations and to transparency towards those who choose us.",
      "We want to bring to the pet market — and in the future to the human market — products developed with clear criteria, without excessive claims and with the same care we would give to an animal or a family member.",
      "The BioNutrix team brings together different backgrounds — chemistry, pharmaceutical, communication — united by the desire to create formulations designed for everyday life.",
    ],
    values_title: "Mission, vision, values",
    mission_h: "Mission",
    mission_t: "Bring clear and cautious formulations to the daily routine of people and animals.",
    vision_h: "Vision",
    vision_t: "An Italian company recognised for quality, transparency and respect towards those who choose it.",
    values_h: "Values",
    values_t: "Scientific expertise, selected ingredients, responsible communication, care.",
    territory_over: "Location",
    territory_h: "A territory, an identity.",
    territory_t:
      "Our headquarters are in Porto Empedocle, Sicily — a land where nature and research have always intersected. From here we coordinate the development of formulations, Italian production and the relationship with commercial partners across the country.",
  },
};

export default function AboutPage() {
  const { lang } = useI18n();
  const c = CONTENT[lang];
  return (
    <div data-testid="about-page">
      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 pt-16 lg:pt-24 pb-8">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-editorial text-muted-ink mb-4">{c.overline}</div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl text-brand leading-[0.98]">
            {c.title}
          </h1>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-8 lg:py-16 grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-7">
          <p className="font-display text-2xl md:text-3xl text-brand leading-snug">{c.lead}</p>
          <div className="mt-8 space-y-5 text-muted-ink leading-relaxed">
            {c.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          </div>
        </div>
        <aside className="md:col-span-5">
          <div className="aspect-[4/5] overflow-hidden bg-cream-warm image-hover-scale">
            <img
              src="https://images.pexels.com/photos/14751278/pexels-photo-14751278.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Convivenza domestica con cane e gatto"
              className="w-full h-full object-cover"
            />
          </div>
        </aside>
      </section>

      <section className="bg-cream-warm py-16 lg:py-20">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-1 md:grid-cols-3 gap-8">
          <Value title={c.mission_h} text={c.mission_t} />
          <Value title={c.vision_h} text={c.vision_t} />
          <Value title={c.values_h} text={c.values_t} />
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-5">
          <div className="text-xs uppercase tracking-editorial text-muted-ink mb-3">{c.territory_over}</div>
          <h2 className="font-display text-4xl text-brand leading-tight">{c.territory_h}</h2>
        </div>
        <div className="md:col-span-7">
          <p className="text-muted-ink leading-relaxed">{c.territory_t}</p>
        </div>
      </section>
    </div>
  );
}

function Value({ title, text }) {
  return (
    <div className="bg-white border border-soft p-8">
      <div className="text-xs uppercase tracking-editorial text-muted-ink mb-3">{title}</div>
      <p className="font-display text-2xl text-brand leading-snug">{text}</p>
    </div>
  );
}
